import React from 'react';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { GENDER } from 'domain/entities/AgentUser/AgentUser';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';
import { GroupedToggleFormSelect } from 'ui/components/elements/FormSelect/GroupedToggleFormSelect';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import { ImageUpload } from 'ui/components/features/ImageUpload/ImageUpload';
import { useAgentUserProfileRegistration } from './useAgentUserProfileRegistration';

const BgWrapper = styled.div`
    background-color: #f8f9fd;
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
`;

const Wrapper = styled.div`
    margin: 0 auto;
    padding: 40px 30px;
    border-radius: 12px;
    background-color: var(--white);
    box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
    width: 600px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-size: var(--font-medium);
    text-align: center;
`;

const Title = styled.div`
    font-size: var(--font-xxx-large);
    color: var(--main-color-blue);
    font-weight: 600;
    margin-bottom: 20px;
    text-align: center;
`;

const ButtonContainer = styled.div`
    margin: 20px auto 0;
`;

const Birthday = styled.div`
    display: flex;
    width: 70%;
    > * {
        flex: 1;
    }

    @media (max-width: 768px) {
        width: 100%;
        > * {
            max-width: 33%;
        }
    }
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const Description = styled.div`
    font-size: 13px;
    font-weight: var(--font-bold);
    color: #626262;
    text-align: left;
`;

export const AgentUserProfileRegistration: React.FC = () => {
    const {
        control,
        loading,
        errors,
        showSnackbar,
        yearOptions,
        monthOptions,
        dayOptions,
        notCompletedProfile,
        setShowSnackbar,
        submit,
        watch,
        setValue,
    } = useAgentUserProfileRegistration();

    return (
        <BgWrapper>
            <Wrapper>
                <SnackBar
                    message="ユーザープロフィールを設定しました。"
                    open={showSnackbar}
                    setOpen={setShowSnackbar}
                />
                <Title>ユーザープロフィール</Title>
                {notCompletedProfile && (
                    <Description>※サービスのご利用を開始するにはプロフィールの登録が必要です</Description>
                )}
                <FlexContainer>
                    <FormLabel title="お名前（姓）" isRequired />
                    <Description>※お名前はマッチングした企業様に公開されます。</Description>
                    <Controller
                        name="last_name"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.last_name?.message} />
                                {errors.last_name?.message && <FormError message={errors.last_name.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="お名前（名）" isRequired />
                    <Controller
                        name="first_name"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.first_name?.message} />
                                {errors.first_name?.message && <FormError message={errors.first_name.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="ふりがな（姓）" isRequired />
                    <Controller
                        name="last_name_kana"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.last_name_kana?.message} />
                                {errors.last_name_kana?.message && (
                                    <FormError message={errors.last_name_kana.message} />
                                )}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="ふりがな（名）" isRequired />
                    <Controller
                        name="first_name_kana"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.first_name_kana?.message} />
                                {errors.first_name_kana?.message && (
                                    <FormError message={errors.first_name_kana.message} />
                                )}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="性別" isRequired />
                    <Controller
                        name="gender"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormSelect
                                    {...field}
                                    options={Object.entries(GENDER).map(([key, value]) => ({
                                        value: key,
                                        label: value,
                                    }))}
                                />
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="電話番号" isRequired />
                    <Description>※ハイフンなしで入力してください。</Description>
                    <Controller
                        name="phone_number"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput
                                    {...field}
                                    value={field.value ?? ''}
                                    hasError={!!errors.phone_number?.message}
                                />
                                {errors.phone_number?.message && <FormError message={errors.phone_number.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="生年月日" />
                    <Birthday>
                        <Controller
                            name="year"
                            control={control}
                            render={({ field }) => (
                                <GroupedToggleFormSelect
                                    {...field}
                                    options={yearOptions}
                                    placeholder="年"
                                    name="年を選択"
                                    error={errors.year?.message || errors.month?.message || errors.day?.message}
                                    position="first"
                                />
                            )}
                        />

                        <Controller
                            name="month"
                            control={control}
                            render={({ field }) => (
                                <GroupedToggleFormSelect
                                    {...field}
                                    options={monthOptions}
                                    placeholder="月"
                                    name="月を選択"
                                    position="middle"
                                />
                            )}
                        />
                        <Controller
                            name="day"
                            control={control}
                            render={({ field }) => (
                                <GroupedToggleFormSelect
                                    {...field}
                                    options={dayOptions}
                                    placeholder="日"
                                    name="日を選択"
                                    position="last"
                                />
                            )}
                        />
                    </Birthday>
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="アイコン画像" />
                    <ImageUpload
                        imagePath={watch('icon_image') ?? ''}
                        directory="agent_profile"
                        fileName={'icon'}
                        fileCategory="agent"
                        disabled={loading}
                        width={180}
                        height={180}
                        onComplete={(fileUrl) => setValue('icon_image', fileUrl)}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="役職" />
                    <Controller
                        name="job"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} value={field.value ?? ''} hasError={!!errors.job?.message} />
                                {errors.job?.message && <FormError message={errors.job.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="部署" />
                    <Controller
                        name="department"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput
                                    {...field}
                                    value={field.value ?? ''}
                                    hasError={!!errors.department?.message}
                                />
                                {errors.department?.message && <FormError message={errors.department.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <ButtonContainer>
                    <ActionButton
                        disabled={loading}
                        onClick={submit}
                        bgColor="main-color-blue"
                        style={{ maxWidth: '400px' }}
                    >
                        登録する
                    </ActionButton>
                </ButtonContainer>
            </Wrapper>
        </BgWrapper>
    );
};
